/** One step in the procedure flow graph (editor or read-only). */
export interface FlowStep {
  key: string
  formName: string
  predecessorKeys: Set<string>
}

export interface FlowLayer {
  depth: number
  nodes: { key: string; formName: string }[]
}

export interface FlowEdge {
  fromKey: string
  toKey: string
  fromName: string
  toName: string
}

/** Assign each step a row depth = longest path from any root. Supports parallel branches. */
export function computeFlowLayers(steps: FlowStep[]): FlowLayer[] {
  if (steps.length === 0) return []

  const byKey = new Map(steps.map(s => [s.key, s]))

  const depthCache = new Map<string, number>()

  function depth(key: string, visiting = new Set<string>()): number {
    const cached = depthCache.get(key)
    if (cached != null) return cached
    if (visiting.has(key)) return 0
    visiting.add(key)

    const step = byKey.get(key)
    if (!step) return 0

    const preds = [...step.predecessorKeys].filter(k => byKey.has(k))
    const d = preds.length === 0 ? 0 : 1 + Math.max(...preds.map(p => depth(p, new Set(visiting))))
    depthCache.set(key, d)
    return d
  }

  for (const s of steps) depth(s.key)

  const maxDepth = Math.max(...[...depthCache.values()])
  const rows: FlowLayer[] = Array.from({ length: maxDepth + 1 }, (_, depth) => ({
    depth,
    nodes: [],
  }))

  for (const s of steps) {
    const d = depthCache.get(s.key) ?? 0
    rows[d].nodes.push({ key: s.key, formName: s.formName })
  }

  return rows
}

export function computeFlowEdges(steps: FlowStep[]): FlowEdge[] {
  const byKey = new Map(steps.map(s => [s.key, s]))
  const edges: FlowEdge[] = []

  for (const step of steps) {
    for (const predKey of step.predecessorKeys) {
      const pred = byKey.get(predKey)
      if (!pred) continue
      edges.push({
        fromKey: predKey,
        toKey: step.key,
        fromName: pred.formName,
        toName: step.formName,
      })
    }
  }

  return edges
}

/** Roots = no predecessors configured. */
export function findRootSteps(steps: FlowStep[]): FlowStep[] {
  return steps.filter(s => s.predecessorKeys.size === 0)
}

/** Steps that list `key` as a predecessor (direct successors). */
export function findSuccessors(steps: FlowStep[], key: string): FlowStep[] {
  return steps.filter(s => s.predecessorKeys.has(key))
}

/**
 * Would adding `predecessorKey` → `stepKey` create a cycle?
 * Used when picking predecessors in the editor.
 */
export function wouldCreateCycle(
  steps: FlowStep[],
  stepKey: string,
  predecessorKey: string,
): boolean {
  if (stepKey === predecessorKey) return true

  const byKey = new Map(steps.map(s => [s.key, s]))

  // Can we reach stepKey starting from predecessorKey by following predecessor links upward?
  const visit = (current: string, seen: Set<string>): boolean => {
    if (current === stepKey) return true
    if (seen.has(current)) return false
    seen.add(current)
    const node = byKey.get(current)
    if (!node) return false
    for (const pred of node.predecessorKeys) {
      if (visit(pred, seen)) return true
    }
    return false
  }

  return visit(predecessorKey, new Set())
}
